import { computed, ref } from 'vue';
import { createTrackingDomainsModule } from '@/store/modules/cache/tracking-domains';
import { createRedirectDomainsModule } from '@/store/modules/cache/redirect-domains';
import { createCampaignsCacheModule } from '@/store/modules/cache/campaigns-cache';
import { createAffiliateNetworksCacheModule } from '@/store/modules/cache/affiliate-networks-cache';
import { createIntegrationsCacheModule } from '@/store/modules/cache/integrations-cache';

type BasicCacheList = {
  data: unknown[];
  isUploaded: boolean;
};

type ExtendedCacheList = BasicCacheList & {
  isAllInCache: boolean;
  uploadedForWorkspace: string | null;
  uploadedForCountryId?: string | null;
};

type TagsCache = {
  campaigns: BasicCacheList;
  offers: BasicCacheList;
  landings: BasicCacheList;
  flows: BasicCacheList;
  'traffic-sources': BasicCacheList;
  'affiliate-networks': BasicCacheList;
};

export const createCacheModule = () => {
  const trackingDomains = createTrackingDomainsModule();
  const redirectDomains = createRedirectDomainsModule();
  const campaignsCache = createCampaignsCacheModule();
  const affiliateNetworksCache = createAffiliateNetworksCacheModule();
  const integrationsCacheModule = createIntegrationsCacheModule();
  const tagsCache = ref<TagsCache>({
    campaigns: { data: [], isUploaded: false },
    offers: { data: [], isUploaded: false },
    landings: { data: [], isUploaded: false },
    flows: { data: [], isUploaded: false },
    'traffic-sources': { data: [], isUploaded: false },
    'affiliate-networks': { data: [], isUploaded: false },
  });
  const offersCache = ref<ExtendedCacheList>({
    data: [],
    isUploaded: false,
    isAllInCache: false,
    uploadedForWorkspace: null,
    uploadedForCountryId: null,
  });
  const landingsCache = ref<ExtendedCacheList>({
    data: [],
    isUploaded: false,
    isAllInCache: false,
    uploadedForWorkspace: null,
    uploadedForCountryId: null,
  });
  const trafficSourcesCache = ref<ExtendedCacheList>({
    data: [],
    isUploaded: false,
    isAllInCache: false,
    uploadedForWorkspace: null,
  });
  const flowsCache = ref<ExtendedCacheList>({
    data: [],
    isUploaded: false,
    isAllInCache: false,
    uploadedForWorkspace: null,
    uploadedForCountryId: null,
  });
  const integrationsCache = integrationsCacheModule.state.integrationsCache;

  const populateOffersCache = (payload: unknown[]) => {
    offersCache.value.data = payload;
    offersCache.value.isAllInCache = (payload?.length ?? 0) < 199;
    offersCache.value.isUploaded = true;
    offersCache.value.uploadedForWorkspace = null;
  };

  const updateOffersCache = ({
    workspaceId,
    countryId,
    data,
  }: {
    workspaceId: string;
    countryId: string | number;
    data: unknown[];
  }) => {
    offersCache.value.data = data;
    offersCache.value.uploadedForWorkspace = workspaceId;
    offersCache.value.uploadedForCountryId = countryId;
  };

  const populateLandingsCache = (payload: unknown[]) => {
    landingsCache.value.data = payload;
    landingsCache.value.isAllInCache = (payload?.length ?? 0) < 199;
    landingsCache.value.isUploaded = true;
    landingsCache.value.uploadedForWorkspace = null;
  };

  const updateLandingsCache = ({
    workspaceId,
    countryId,
    data,
  }: {
    workspaceId: string;
    countryId: string | number;
    data: unknown[];
  }) => {
    landingsCache.value.data = data;
    landingsCache.value.uploadedForWorkspace = workspaceId;
    landingsCache.value.uploadedForCountryId = countryId;
  };

  const populateFlowsCache = (payload: unknown[]) => {
    flowsCache.value.data = payload;
    flowsCache.value.isAllInCache = (payload?.length ?? 0) < 199;
    flowsCache.value.isUploaded = true;
    flowsCache.value.uploadedForWorkspace = null;
  };

  const updateFlowsCache = ({
    workspaceId,
    countryId,
    data,
  }: {
    workspaceId: string;
    countryId: string | number;
    data: unknown[];
  }) => {
    flowsCache.value.data = data;
    flowsCache.value.uploadedForWorkspace = workspaceId;
    flowsCache.value.uploadedForCountryId = countryId;
  };

  const populateTrafficSourcesCache = (payload: unknown[]) => {
    trafficSourcesCache.value.data = payload;
    trafficSourcesCache.value.isAllInCache = (payload?.length ?? 0) < 199;
    trafficSourcesCache.value.isUploaded = true;
    trafficSourcesCache.value.uploadedForWorkspace = null;
  };

  const updateTrafficSourcesCache = ({
    workspaceId,
    data,
  }: {
    workspaceId: string;
    data: unknown[];
  }) => {
    trafficSourcesCache.value.data = data;
    trafficSourcesCache.value.uploadedForWorkspace = workspaceId;
  };

  const cacheStartUpdating = ({
    cacheProperty,
    cachePropertyLevel2,
  }: {
    cacheProperty?: string;
    cachePropertyLevel2?: string;
  }) => {
    if (cacheProperty && cachePropertyLevel2) {
      const tagsState = tagsCache.value[cachePropertyLevel2 as keyof TagsCache];
      if (!tagsState) return;
      tagsState.isUploaded = false;
      return;
    }
    if (!cacheProperty) return;
    const cacheState = cache.value[cacheProperty as keyof typeof cache.value] as
      | { isUploaded?: boolean }
      | undefined;
    if (!cacheState) return;
    if (typeof cacheState.isUploaded === 'undefined') return;
    cacheState.isUploaded = false;
  };

  const cache = computed(() => ({
    campaigns: campaignsCache.state.campaignsCache.value,
    affiliateNetworks: affiliateNetworksCache.state.affiliateNetworksCache.value,
    offers: offersCache.value,
    landings: landingsCache.value,
    trafficSources: trafficSourcesCache.value,
    flows: flowsCache.value,
    integrations: integrationsCache.value,
    tags: tagsCache.value,
    trackingDomains: trackingDomains.state.trackingDomains.value,
    redirectDomains: redirectDomains.state.redirectDomains.value,
    cacheStartUpdating,
    populateAffiliateNetworksCache: affiliateNetworksCache.state.populateAffiliateNetworksCache,
    populateCampaignsCache: campaignsCache.state.populateCampaignsCache,
    populateOffersCache,
    updateOffersCache,
    populateLandingsCache,
    updateLandingsCache,
    populateFlowsCache,
    updateFlowsCache,
    populateTrafficSourcesCache,
    updateTrafficSourcesCache,
    populateTagsCache: (payload: {
      type: keyof TagsCache;
      data: unknown[];
    }) => {
      const tagsState = tagsCache.value[payload.type];
      if (!tagsState) return;
      tagsState.data = payload.data;
      tagsState.isUploaded = true;
    },
    populateIntegrationsCache: integrationsCacheModule.state.populateIntegrationsCache,
    updateIntegrationsCache: integrationsCacheModule.state.updateIntegrationsCache,
    updateCampaignsCache: campaignsCache.state.updateCampaignsCache,
  }));

  const reset = () => {
    trackingDomains.reset();
    redirectDomains.reset();
    campaignsCache.reset();
    affiliateNetworksCache.reset();
    integrationsCacheModule.reset();
    tagsCache.value = {
      campaigns: { data: [], isUploaded: false },
      offers: { data: [], isUploaded: false },
      landings: { data: [], isUploaded: false },
      flows: { data: [], isUploaded: false },
      'traffic-sources': { data: [], isUploaded: false },
      'affiliate-networks': { data: [], isUploaded: false },
    };
    offersCache.value = {
      data: [],
      isUploaded: false,
      isAllInCache: false,
      uploadedForWorkspace: null,
      uploadedForCountryId: null,
    };
    landingsCache.value = {
      data: [],
      isUploaded: false,
      isAllInCache: false,
      uploadedForWorkspace: null,
      uploadedForCountryId: null,
    };
    trafficSourcesCache.value = {
      data: [],
      isUploaded: false,
      isAllInCache: false,
      uploadedForWorkspace: null,
    };
    flowsCache.value = {
      data: [],
      isUploaded: false,
      isAllInCache: false,
      uploadedForWorkspace: null,
      uploadedForCountryId: null,
    };
  };

  return {
    state: {
      ...trackingDomains.state,
      ...redirectDomains.state,
      ...affiliateNetworksCache.state,
      cache,
    },
    reset,
  };
};
